type QueueType = "fifo" | "lifo"; // First In First Out, Last In First Out

/**
 * The `MultiRequestQueueManager` class manages multiple asynchronous requests,
 * limiting concurrent requests based on a defined maximum and supporting FIFO/LIFO queue types.
 */
class MultiRequestQueueManager {
    private maxConcurrent: number;
    private currentConcurrent: number = 0;
    private queue: { request: () => Promise<any>; keys?: string[] }[] = [];
    private activeKeys: Set<string> = new Set();
    private processedKeysHistory: Set<string> = new Set();
    private queueType: QueueType;

    /**
     * Creates an instance of MultiRequestQueueManager.
     *
     * @param queueType - The type of queue to use ("fifo" for First In First Out or "lifo" for Last In First Out).
     * @param maxConcurrent - The maximum number of concurrent requests (default is 4).
     */
    public constructor(queueType: QueueType, maxConcurrent: number = 4) {
        this.queueType = queueType;
        this.maxConcurrent = maxConcurrent;
    }

    /**
     * Adds a new request to the queue and initiates processing if allowed.
     *
     * @param request - The function that returns a promise for the request.
     * @param keys - Optional array of unique keys associated with the request.
     */
    public addRequest(request: () => Promise<any>, keys?: string[]): void {
        if (keys) {
            keys.forEach(key => this.activeKeys.add(key));
        }

        this.queue.push({ request, keys });

        this.runNext();
    }

    /**
     * Checks if a request with a specific key is currently active.
     *
     * @param key - The unique key of the request to check.
     * @returns True if the request is active, false otherwise.
     */
    public isRequestActive(key: string): boolean {
        return this.activeKeys.has(key);
    }

    /**
     * Checks if a request with a specific key has been processed.
     *
     * @param key - The unique key of the request to check.
     * @returns True if the request has been processed, false otherwise.
     */
    public isRequestProcessed(key: string): boolean {
        return this.processedKeysHistory.has(key);
    }

    private runNext(): void {
        if (this.currentConcurrent < this.maxConcurrent && this.queue.length > 0) {
            const task = this.queueType == "fifo" ? this.queue.shift() : this.queue.pop();

            if (task) {
                const { request, keys } = task;
                this.currentConcurrent++;

                request()
                    .then(() => {
                        if (keys) {
                            keys.forEach(key => {
                                this.processedKeysHistory.add(key);
                            });
                        }
                    })
                    .catch(error => {
                        console.error("Queue manager request failed:", error);
                    })
                    .finally(() => {
                        if (keys) {
                            keys.forEach(key => {
                                this.activeKeys.delete(key);
                            });
                        }
                        this.currentConcurrent--;
                        this.runNext();
                    });
            }
        }
    }
}

export default MultiRequestQueueManager;
